import { useMemo } from 'react'
import { MutationHookOptions, QueryHookOptions } from '@apollo/client'
import {
  ProductQuery,
  ProductQueryVariables,
  ProductUpdateMutation,
  ProductUpdateMutationVariables,
  ProductVariant as ProductVariantObject,
  ProductVariantDeleteMutationVariables,
  ProductVariantUpdateMutationVariables,
  useProductDeleteMutation,
  useProductQuery,
  useProductUpdateMutation,
  useProductVariantDeleteMutation,
  useProductVariantUpdateMutation,
} from 'lib/graphql/storeadmin'
import { Options } from './interfaces'
import {
  ProductOptionsProps,
  OptionsValue,
  Variant,
  ProductOptionsInterFace,
} from 'components/products'

export type {
  ProductUpdateMutationVariables,
  ProductUpdateInput,
  ProductVariantsUpdateInput,
  ProductDeleteMutationVariables,
  ProductVariantUpdateMutationVariables,
  ProductVariantDeleteMutationVariables,
} from 'lib/graphql/storeadmin/types-and-hooks'

export type ProductVariant = Omit<
  ProductVariantObject,
  '__typename' | 'createdAt' | 'updatedAt'
>

export interface Product
  extends Omit<
    ProductQuery['product'],
    '__typename' | 'variants' | 'createdAt' | 'updatedAt'
  > {
  variants: Array<ProductVariant>
}

type ProductQueryHookOptions = QueryHookOptions<
  ProductQuery,
  ProductQueryVariables
>
type ProductMutationHookOptions = MutationHookOptions<
  ProductUpdateMutation,
  ProductUpdateMutationVariables,
  ProductVariantUpdateMutationVariables,
  // eslint-disable-next-line
  // @ts-ignore
  ProductVariantDeleteMutationVariables
>

export type UseProductOptions = Options<
  ProductQueryHookOptions,
  // eslint-disable-next-line
  // @ts-ignore
  ProductMutationHookOptions
>

type ProductQueryReturnType = Omit<ReturnType<typeof useProductQuery>, 'data'>
type ProductUpdateReturnType = ReturnType<typeof useProductUpdateMutation>
type ProductDeleteReturnType = ReturnType<typeof useProductDeleteMutation>
type ProductVariantUpdateType = ReturnType<
  typeof useProductVariantUpdateMutation
>
type ProductVariantDeleteType = ReturnType<typeof useProductDeleteMutation>

export interface UseProductResult extends ProductQueryReturnType {
  product: Product | null
  fileUploadUrl: string
  update: ProductUpdateReturnType[0]
  updateResult: ProductUpdateReturnType[1]
  remove: ProductDeleteReturnType[0]
  removeResult: ProductDeleteReturnType[1]
  variantUpdate: ProductVariantUpdateType[0]
  variantUpdateResult: ProductVariantUpdateType[1]
  variantDelete: ProductVariantDeleteType[0]
  variantDeleteResult: ProductVariantDeleteType[1]
}

interface ProductVariantsFormat {
  productsOptions: ProductOptionsProps[] | ProductOptionsInterFace[]
  productsVariants?: Variant[]
  editedProductVariants?: Variant[]
  productsOptionsValue?: OptionsValue[][] | null
}

interface CheckTypeAndPositionProps {
  index?: number
  productsVariants?: Variant[]
  productsOptionsValue: OptionsValue[][]
  data?: string | number | boolean | null
  defaultData?: string | number | boolean | null
}

export function useProduct(
  id: string,
  options: UseProductOptions = {}
): UseProductResult {
  const queryVariables = { id }
  const { data, refetch, ...queryResults } = useProductQuery({
    variables: queryVariables,
    onCompleted: options.onQueryCompleted,
    onError: options.onQueryError,
    context: options.context,
    pollInterval: options.pollInterval || 0,
    ssr: options.ssr || true,
  })
  const internalProduct = useMemo(() => data?.product, [data])
  const internalVariants = useMemo(
    () => internalProduct?.variants?.edges?.map(({ variant }) => variant),
    [internalProduct]
  )
  const product = internalProduct
    ? { ...internalProduct, variants: internalVariants }
    : null

  const [update, updateResult] = useProductUpdateMutation({
    context: options.context,
    onCompleted: (v) => {
      refetch(queryVariables)
      options?.onMutationCompleted?.(v)
    },
    onError: options.onMutationError,
    onQueryUpdated: options.onQueryUpdated,
  })

  const [remove, removeResult] = useProductDeleteMutation({
    context: options.context,
    onCompleted: (v) => {
      refetch(queryVariables)
      options?.onMutationCompleted?.(v)
    },
    onError: options.onMutationError,
    onQueryUpdated: options.onQueryUpdated,
  })

  const [variantUpdate, variantUpdateResult] = useProductVariantUpdateMutation({
    context: options.context,
    onCompleted: (v) => {
      refetch(queryVariables)
      options?.onMutationCompleted?.(v)
    },
    onError: options.onMutationError,
    onQueryUpdated: options.onQueryUpdated,
  })

  const [variantDelete, variantDeleteResult] = useProductVariantDeleteMutation({
    context: options.context,
    onCompleted: (v) => {
      refetch(queryVariables)
      options?.onMutationCompleted?.(v)
    },
    onError: options.onMutationError,
    onQueryUpdated: options.onQueryUpdated,
  })

  return {
    // eslint-disable-next-line
    // @ts-ignore
    product,
    fileUploadUrl: data?.fileUploadUrl,
    refetch,
    ...queryResults,
    update,
    updateResult,
    remove,
    removeResult,
    variantUpdate,
    variantUpdateResult,
    variantDelete,
    variantDeleteResult,
  }
}

function checkTypeAndPosition({
  productsVariants,
  productsOptionsValue,
  data,
  defaultData,
  index,
}: CheckTypeAndPositionProps) {
  if (
    typeof productsOptionsValue !== 'undefined' &&
    productsVariants[index]?.position
  ) {
    return data
  } else {
    return defaultData
  }
}

export function productVariantsFormat({
  productsOptions,
  productsVariants,
  productsOptionsValue,
  editedProductVariants,
}: ProductVariantsFormat) {
  /*
    Check the options length & data length.
    use for loop multiple time depends of length on options,
    in loop create a data structure as shopify variation format
   */

  const variants = []
  const storeVariants = []
  const activeOptionValue = []

  productsOptions?.forEach((productsOptions) => {
    activeOptionValue.push(
      productsOptions?.productOptionsValues?.filter((v) => v !== undefined)
    )
  })

  if (
    activeOptionValue?.length > 2 &&
    productsOptions?.length > 2 &&
    typeof productsOptions[1]?.productOptionsValues !== 'undefined' &&
    typeof productsOptions[2]?.productOptionsValues !== 'undefined'
  ) {
    activeOptionValue[0]?.forEach((optionValueOne, index) => {
      activeOptionValue[1]?.forEach((optionValueTwo) => {
        activeOptionValue[2]?.forEach((optionValueThree) => {
          if (
            optionValueOne?.optionsValue !== '' &&
            optionValueTwo?.optionsValue !== '' &&
            optionValueThree?.optionsValue !== ''
          ) {
            const beforeAddNewValue = `${optionValueOne?.optionsValue} / ${optionValueTwo?.optionsValue}`

            const selectedOptions =
              typeof optionValueThree?.optionsValue !== 'undefined'
                ? `${optionValueOne?.optionsValue} / ${optionValueTwo?.optionsValue} / ${optionValueThree?.optionsValue}`
                : beforeAddNewValue

            const currentIndex = editedProductVariants?.findIndex(
              (obj) =>
                obj.selectedOptions === selectedOptions ||
                obj.selectedOptions === beforeAddNewValue
            )

            const editedData = editedProductVariants?.[currentIndex]

            const data =
              productsVariants[Number(productsVariants[index]?.position) - 1]
            const key = `${optionValueOne?.optionsValue}${optionValueTwo?.optionsValue}${optionValueThree?.optionsValue}${index}`

            variants.push({
              key: key,
              unit:
                editedData?.unit ||
                checkTypeAndPosition({
                  productsVariants: productsVariants,
                  productsOptionsValue: productsOptionsValue,
                  data: data?.unit,
                  defaultData: productsVariants[0]?.unit,
                  index: index,
                }),
              price:
                Number(editedData?.price) ||
                checkTypeAndPosition({
                  productsVariants: productsVariants,
                  productsOptionsValue: productsOptionsValue,
                  data: data?.price,
                  defaultData: productsVariants[0]?.price,
                  index: index,
                }),
              periodForSale: editedData?.periodForSale || undefined,
              specialPrice:
                Number(editedData?.specialPrice) ||
                checkTypeAndPosition({
                  productsVariants: productsVariants,
                  productsOptionsValue: productsOptionsValue,
                  data: Number(data?.specialPrice || 0),
                  defaultData: productsVariants[0]?.specialPrice,
                  index: index,
                }),
              inventory:
                Number(editedData?.inventory) ||
                checkTypeAndPosition({
                  productsVariants: productsVariants,
                  productsOptionsValue: productsOptionsValue,
                  data: data?.inventory,
                  defaultData: productsVariants[0]?.inventory,
                  index: index,
                }),
              availableForSale:
                editedData?.availableForSale ||
                checkTypeAndPosition({
                  productsVariants: productsVariants,
                  productsOptionsValue: productsOptionsValue,
                  data: data?.availableForSale,
                  defaultData: productsVariants[0]?.availableForSale,
                  index: index,
                }),
              selectedOptions: selectedOptions,
            })
            storeVariants.push(
              optionValueOne?.optionsValue,
              optionValueTwo?.optionsValue,
              optionValueThree?.optionsValue
            )
          }
        })
      })
    })

    return {
      variants: variants,
      activeOptionValue: activeOptionValue,
      storeVariants: storeVariants,
    }
  } else if (
    activeOptionValue[0]?.length > 0 &&
    productsOptions.length > 1 &&
    typeof productsOptions[1]?.productOptionsValues !== 'undefined' &&
    typeof productsOptions[2]?.productOptionsValues === 'undefined'
  ) {
    activeOptionValue[0]?.forEach((optionValueOne, index) => {
      activeOptionValue[1]?.forEach((optionValueTwo) => {
        if (
          optionValueOne?.optionsValue !== '' &&
          optionValueTwo?.optionsValue !== ''
        ) {
          const beforeAddNewValue = `${optionValueOne?.optionsValue}`
          const selectedOptions =
            typeof optionValueTwo?.optionsValue !== 'undefined'
              ? `${optionValueOne?.optionsValue} / ${optionValueTwo?.optionsValue}`
              : beforeAddNewValue

          const currentIndex = editedProductVariants?.findIndex(
            (obj) =>
              obj.selectedOptions === selectedOptions ||
              obj.selectedOptions === beforeAddNewValue
          )

          const editedData = editedProductVariants?.[currentIndex]

          const data =
            productsVariants[Number(productsVariants[index]?.position) - 1]

          const key = `${optionValueOne?.optionsValue}${optionValueTwo?.optionsValue}${index}`

          variants.push({
            key: key,
            unit:
              editedData?.unit ||
              checkTypeAndPosition({
                productsVariants: productsVariants,
                productsOptionsValue: productsOptionsValue,
                data: data?.unit,
                defaultData: productsVariants[0]?.unit,
                index: index,
              }),
            price:
              Number(editedData?.price) ||
              checkTypeAndPosition({
                productsVariants: productsVariants,
                productsOptionsValue: productsOptionsValue,
                data: data?.price,
                defaultData: productsVariants[0]?.price,
                index: index,
              }),
            periodForSale: editedData?.periodForSale || undefined,
            specialPrice:
              Number(editedData?.specialPrice) ||
              checkTypeAndPosition({
                productsVariants: productsVariants,
                productsOptionsValue: productsOptionsValue,
                data: Number(data?.specialPrice || 0),
                defaultData: productsVariants[0]?.specialPrice,
                index: index,
              }),
            inventory:
              Number(editedData?.inventory) ||
              checkTypeAndPosition({
                productsVariants: productsVariants,
                productsOptionsValue: productsOptionsValue,
                data: data?.inventory,
                defaultData: productsVariants[0]?.inventory,
                index: index,
              }),
            availableForSale:
              editedData?.availableForSale ||
              checkTypeAndPosition({
                productsVariants: productsVariants,
                productsOptionsValue: productsOptionsValue,
                data: data?.availableForSale,
                defaultData: productsVariants[0]?.availableForSale,
                index: index,
              }),
            selectedOptions: selectedOptions,
          })
          storeVariants.push(
            optionValueOne?.optionsValue,
            optionValueTwo?.optionsValue
          )
        }
      })
    })

    return {
      variants: variants,
      activeOptionValue: activeOptionValue,
      storeVariants: storeVariants,
    }
  } else {
    activeOptionValue[0]?.forEach((optionValueOne, index) => {
      if (optionValueOne?.optionsValue !== '') {
        const selectedOptions = `${optionValueOne?.optionsValue}`

        const editedData =
          selectedOptions ===
          editedProductVariants?.[index]?.selectedOptions.toString()
            ? editedProductVariants?.[index]
            : undefined

        const data =
          productsVariants[Number(productsVariants[index]?.position) - 1]

        const key = productVariantsFormat[index]?.key

        variants.push({
          key: key ? key : `${optionValueOne?.optionsValue}${index}`,
          unit:
            editedData?.unit ||
            checkTypeAndPosition({
              productsVariants: productsVariants,
              productsOptionsValue: productsOptionsValue,
              data: data?.unit,
              defaultData: productsVariants[0]?.unit,
              index: index,
            }),
          price:
            Number(editedData?.price) ||
            checkTypeAndPosition({
              productsVariants: productsVariants,
              productsOptionsValue: productsOptionsValue,
              data: Number(data?.price || 0),
              defaultData: productsVariants[0]?.price,
              index: index,
            }),
          periodForSale: editedData?.periodForSale || undefined,
          specialPrice:
            Number(editedData?.specialPrice) ||
            checkTypeAndPosition({
              productsVariants: productsVariants,
              productsOptionsValue: productsOptionsValue,
              data: Number(data?.specialPrice || 0),
              defaultData: productsVariants[0]?.specialPrice,
              index: index,
            }),
          inventory:
            Number(editedData?.inventory) ||
            checkTypeAndPosition({
              productsVariants: productsVariants,
              productsOptionsValue: productsOptionsValue,
              data: Number(data?.inventory || 0),
              defaultData: productsVariants[0]?.inventory,
              index: index,
            }),
          availableForSale:
            editedData?.availableForSale ||
            checkTypeAndPosition({
              productsVariants: productsVariants,
              productsOptionsValue: productsOptionsValue,
              data: data?.availableForSale,
              defaultData: productsVariants[0]?.availableForSale,
              index: index,
            }),
          selectedOptions:
            typeof optionValueOne?.optionsValue !== 'undefined'
              ? selectedOptions
              : '',
        })
        storeVariants.push(optionValueOne?.optionsValue)
      }
    })

    return {
      variants: variants,
      activeOptionValue: activeOptionValue,
      storeVariants: storeVariants,
    }
  }
}

// Common error handle function for products
export function productsErrorHandling(userErrors): string[] {
  const errorMessages = userErrors.map((value) => {
    switch (value.code) {
      case 'BLANK':
        return '入力値が空白です。'

      case 'CONTAINS_HTML_TAGS':
        return '入力値にHTMLタグが含まれています。'

      case 'CONTAINS_URL':
        return '入力値にURLが含まれています。'

      // TODO: API 側で利用ケースがなければ削除
      case 'PRODUCT_DISABLED':
        return '利用のできない施設です。'

      case 'INVALID':
        if (value.field.indexOf('name') !== -1) {
          return 'タイトルを入力してください。'
        }
        return '適切な値を入力してください。'

      case 'NOT_FOUND':
        return '対象を見つけることができません。'

      case 'TAKEN':
        return '入力値はすでに使用されています。'

      case 'TOO_SHORT' || 'TOO_LONG':
        return '入力値が短すぎたり長すぎたりしないことを確認してください。'

      case 'UNDEFINED':
        return '入力値のデータは登録されていません。'

      case 'UNREGISTERED_PRODUCT':
        return '未登録の施設です。'

      case 'NOTHING_AVAILABLE':
        return 'バリエーションの全てを販売停止にはできません'

      default:
        return '予期せぬエラーが発生しました。'
    }
  })
  // Filter Duplicate item and return unique Array
  return errorMessages?.filter((e, i) => errorMessages?.indexOf(e) === i)
}
